import React, { useState, useLayoutEffect, useEffect, useRef } from "react";
import { auth} from "../../config/firebase";
import { signInWithEmailAndPassword, GoogleAuthProvider, signInWithPopup, AuthError, OAuthProvider, User, createUserWithEmailAndPassword, reauthenticateWithPopup } from "firebase/auth";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { config, getFirebaseErrorMessage } from "../../config/nova-constants";
import { createUserIfNotExists, checkUserAvailabilityForAuth } from "../../services/api/user.service";
import { publicApiFetch } from "../../services/api/client";
import { useAuth } from "./AuthContext";
import { useUser } from "../../contexts/UserContext";
import { PLATFORMS } from "../../constants/platforms";
import { COUNTRY_CODES } from "../../constants/countryCodes";
import CountryCodeDropdown from "../UI/CountryCodeDropdown";
import { X } from "lucide-react";
import { toast } from "react-toastify";
import { trackEvent, setUserId, AUTH_EVENTS } from "../../services/analytics";
import { trackHyperDXAction, setHyperDXUser } from "../../services/observability";
import { normalizeInstagramProfile } from "../../utils/instagramProfile";

const toStoredUsername = (raw: string) =>
  raw.toLowerCase().replace(/[^a-z0-9._-]/g, '').slice(0, 30) || 'user';

const SignupForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const invitationToken = params.get("token");
  const isInvitationSignup = !!invitationToken;
  const { isAuthenticated } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [formError, setFormError] = useState("");
  const [fullName, setFullName] = useState("");
  const [fullNameError, setFullNameError] = useState("");
  const [emailStatus, setEmailStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle');
  const emailTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [mobileStatus, setMobileStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle');
  const mobileTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [spotifyUrl, setSpotifyUrl] = useState("");
  const [spotifyUrlError, setSpotifyUrlError] = useState("");
  const [distributors, setDistributors] = useState<string[]>([]); // Array of distributor names
  const [selectedDistributorDropdown, setSelectedDistributorDropdown] = useState(""); // Selected value from dropdown
  const [customDistributorInput, setCustomDistributorInput] = useState(""); // Input for custom distributor when "Other" is selected
  const [currentDistributorError, setCurrentDistributorError] = useState("");
  const { setAuthStatus } = useAuth();
  const { setSignupInProgress, triggerProfileFetch } = useUser();
  const [loading, setLoading] = useState(false);
  const [emailDisabled, setEmailDisabled] = useState(false);
  const [loadingInvitationPreview, setLoadingInvitationPreview] = useState(false);
  const [mobileNumber, setMobileNumber] = useState("");
  const [mobileNumberError, setMobileNumberError] = useState("");
  const [selectedCountryCode, setSelectedCountryCode] = useState("+1");
  const [countryCodeError, setCountryCodeError] = useState("");
  const [address, setAddress] = useState("");
  const [addressError, setAddressError] = useState("");
  const [country, setCountry] = useState("");
  const [countryError, setCountryError] = useState("");
  const [state, setState] = useState("");
  const [stateError, setStateError] = useState("");
  const [city, setCity] = useState("");
  const [cityError, setCityError] = useState("");
  const [street, setStreet] = useState("");
  const [streetError, setStreetError] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [zipCodeError, setZipCodeError] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [dateOfBirthError, setDateOfBirthError] = useState("");
  const [instagramUrl, setInstagramUrl] = useState("");
  const [instagramUrlError, setInstagramUrlError] = useState("");
  const [appleUrl, setAppleUrl] = useState("");
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [tiktokUrl, setTiktokUrl] = useState("");

  // Track if signup_started has been fired this session
  const signupStartedTrackedRef = useRef(false);

  // Track signup started when user first interacts with form
  const trackSignupStarted = () => {
    if (!signupStartedTrackedRef.current) {
      signupStartedTrackedRef.current = true;
      trackEvent(AUTH_EVENTS.SIGNUP_STARTED, { method: 'email' });
      trackHyperDXAction('auth.signup_started', { method: 'email' });
    }
  };

  // useLayoutEffect(() => {
  //   const timeout = setTimeout(() => {
  //     document.body.style.overflow = 'hidden';
  //   }, 0); // let ResizeObserver finish
  
  //   return () => {
  //     clearTimeout(timeout);
  //     setTimeout(() => {
  //       document.body.style.overflow = '';
  //     }, 0);
  //   };
  // }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (emailTimeoutRef.current) {
        clearTimeout(emailTimeoutRef.current);
      }
      if (mobileTimeoutRef.current) {
        clearTimeout(mobileTimeoutRef.current);
      }
    };
  }, []);

  // Fetch invitation preview when token is present and user is not authenticated
  useEffect(() => {
    const fetchInvitePreview = async () => {
      if (!invitationToken || isAuthenticated) return;
      
      setLoadingInvitationPreview(true);
      try {
        const res = await publicApiFetch(`/unauth/invitations/${invitationToken}/preview`);
        if (res.data && res.data.email) {
          setEmail(res.data.email); // Pre-fill email
          setEmailStatus('available'); // Don't show "check availability" UI
          setEmailDisabled(true); // Prevent editing
        }
      } catch (e: any) {
        // Show "invitation invalid / expired" UI and block signup
        setFormError("This invitation link is invalid or has expired.");
        setEmailDisabled(false); // Allow editing if preview fails
      } finally {
        setLoadingInvitationPreview(false);
      }
    };

    if (isInvitationSignup && !isAuthenticated) {
      fetchInvitePreview();
    }
  }, [invitationToken, isInvitationSignup, isAuthenticated]);

  const validateEmail = (email: string, validationType: 'not_empty' | 'valid_email'): boolean => {
    if (!email.trim()) {
      setEmailError("Email is required");
      setEmailStatus('idle');
      return false;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email) && validationType === 'valid_email') {
      setEmailError("Please enter a valid email");
      setEmailStatus('idle');
      return false;
    }

    // If we're just checking for not_empty, don't validate further
    if (validationType === 'not_empty') {
      return true;
    }

    // For valid_email, check if it's available
    if (emailStatus === 'taken') {
      return false;
    }

    setEmailError("");
    return true;
  };

  const checkEmailAvailability = async (email: string) => {
    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailStatus('idle');
      return;
    }

    setEmailStatus('checking');
    try {
      // For signup, don't pass userId (undefined) to check if email exists for any user
      const response = await checkUserAvailabilityForAuth("email", email);
      if (response.exists) {
        setEmailStatus('taken');
        setEmailError("Email is already registered");
      } else {
        setEmailStatus('available');
        setEmailError("");
      }
    } catch (error) {
      console.error("Error checking email availability:", error);
      setEmailStatus('idle');
      setEmailError("Error checking email availability");
    }
  };

  const debouncedCheckEmail = (email: string) => {
    // Don't check if email is disabled (invitation signup)
    if (emailDisabled) {
      return;
    }
    
    // Clear existing timeout
    if (emailTimeoutRef.current) {
      clearTimeout(emailTimeoutRef.current);
    }

    // Set new timeout
    emailTimeoutRef.current = setTimeout(() => {
      checkEmailAvailability(email);
    }, 300);
  };

  const checkMobileAvailability = async (mobile: string, countryCode?: string) => {
    // Remove all non-digit characters for validation
    const cleanNumber = mobile.replace(/\D/g, '');
    
    // Updated validation: 10-12 digits
    if (cleanNumber.length < 10 || cleanNumber.length > 12) {
      setMobileStatus('idle');
      return;
    }

    const codeToUse = countryCode || selectedCountryCode;
    const fullMobileNumber = `${codeToUse}${cleanNumber}`;
    setMobileStatus('checking');
    try {
      // For signup, don't pass userId (undefined) to check if mobile exists for any user
      const response = await checkUserAvailabilityForAuth("mobile", fullMobileNumber);
      if (response.exists) {
        setMobileStatus('taken');
        setMobileNumberError("Mobile number is already registered");
      } else {
        setMobileStatus('available');
        setMobileNumberError("");
      }
    } catch (error) {
      console.error("Error checking mobile availability:", error);
      setMobileStatus('idle');
      setMobileNumberError("Error checking mobile availability");
    }
  };

  const debouncedCheckMobile = (mobile: string, countryCode?: string) => {
    // Clear existing timeout
    if (mobileTimeoutRef.current) {
      clearTimeout(mobileTimeoutRef.current);
    }

    // Set new timeout
    mobileTimeoutRef.current = setTimeout(() => {
      checkMobileAvailability(mobile, countryCode);
    }, 300);
  };

  const validateSpotifyUrl = (spotifyUrl: string, _validationType?: 'not_empty' | 'valid_spotify_url'): boolean => {
    if (!spotifyUrl.trim()) {
      setSpotifyUrlError("Spotify URL is required");
      return false;
    }
    const trimmed = spotifyUrl.trim();
    const urlPattern = /^https:\/\/open\.spotify\.com\/artist\/[a-zA-Z0-9]+.*$/;
    const uriPattern = /^spotify:artist:[a-zA-Z0-9]+$/;
    if (!urlPattern.test(trimmed) && !uriPattern.test(trimmed)) {
      setSpotifyUrlError("Enter a Spotify artist link with artist ID (e.g. https://open.spotify.com/artist/4Z8W4fKeB5YxbusRsdQVPb or spotify:artist:4Z8W4fKeB5YxbusRsdQVPb).");
      return false;
    }
    setSpotifyUrlError("");
    return true;
  };

  const validateInstagramProfile = (value: string): boolean => {
    const normalized = normalizeInstagramProfile(value);
    if (!normalized) {
      setInstagramUrlError('Instagram username or profile URL is required');
      return false;
    }
    setInstagramUrlError('');
    return true;
  };

  const validateCurrentDistributor = (currentDistributor: string, validationType: 'not_empty' | 'valid_current_distributor'): boolean => {
    if (distributors.length === 0) {
      setCurrentDistributorError("At least one distributor is required");
      return false;
    }
    
    setCurrentDistributorError("");
    return true;
  };

  // Add distributor function
  const addDistributor = (distributor: string) => {
    if (!distributor.trim()) return;
    
    const trimmedDistributor = distributor.trim();
    
    // Check if already exists (case-insensitive)
    if (distributors.some(d => d.toLowerCase() === trimmedDistributor.toLowerCase())) {
      toast.error("Distributor already added");
      return;
    }
    
    // Normalize: check if it matches a platform name (case-insensitive)
    const matchedPlatform = PLATFORMS.find(p => p.name.toLowerCase() === trimmedDistributor.toLowerCase());
    const normalizedDistributor = matchedPlatform ? matchedPlatform.name : trimmedDistributor;
    
    setDistributors([...distributors, normalizedDistributor]);
    setSelectedDistributorDropdown(""); // Reset dropdown
    setCustomDistributorInput(""); // Reset custom input
    if (currentDistributorError) validateCurrentDistributor("", 'not_empty'); // Re-validate
  };

  // Remove distributor function
  const removeDistributor = (index: number) => {
    setDistributors(distributors.filter((_, i) => i !== index));
    if (currentDistributorError) validateCurrentDistributor("", 'not_empty'); // Re-validate
  };

  // Handle distributor selection from dropdown
  const handleDistributorSelect = (value: string) => {
    if (value === "Other") {
      // Don't add "Other" directly, wait for custom input
      setSelectedDistributorDropdown(value);
    } else if (value) {
      addDistributor(value);
    }
  };

  // Handle adding custom distributor
  const handleAddCustomDistributor = () => {
    if (customDistributorInput.trim()) {
      addDistributor(customDistributorInput.trim());
    } else {
      toast.error("Please enter a distributor name");
    }
  };

  const validateFullName = (fullName: string): boolean => {
    if (!fullName.trim()) {
      setFullNameError("Full legal name is required");
      return false;
    }
    setFullNameError("");
    return true;
  };

  const validatePassword = (password: string): boolean => {
    if (!password.trim()) {
      setPasswordError("Password is required");
      return false;
    }
    if (password.length < 6) {
      setPasswordError("Password must be at least 6 characters");
      return false;
    }
    setPasswordError("");
    return true;
  };

  const validateConfirmPassword = (confirmPassword: string): boolean => {
    if (!confirmPassword.trim()) {
      setConfirmPasswordError("Confirm Password is required");
      return false;
    }
    if (confirmPassword !== password) {
      setConfirmPasswordError("Passwords do not match");
      return false;
    }
    setConfirmPasswordError("");
    return true;
  };

  const validateMobileNumber = (mobileNumber: string): boolean => {
    if (!mobileNumber.trim()) {
      setMobileNumberError("Mobile number is required");
      return false;
    }
    
    // Remove all non-digit characters for validation
    const cleanNumber = mobileNumber.replace(/\D/g, '');
    
    // Updated validation: 10-12 digits
    if (cleanNumber.length < 10) {
      setMobileNumberError("Mobile number must be at least 10 digits");
      return false;
    }
    
    if (cleanNumber.length > 12) {
      setMobileNumberError("Mobile number cannot exceed 12 digits");
      return false;
    }

    // Check if mobile is taken
    if (mobileStatus === 'taken') {
      return false;
    }
    
    setMobileNumberError("");
    return true;
  };

  const validateCountryCode = (countryCode: string): boolean => {
    if (!countryCode.trim()) {
      setCountryCodeError("Country code is required");
      return false;
    }
    
    // Check if the country code exists in our list
    const isValidCode = COUNTRY_CODES.some(country => country.code === countryCode);
    if (!isValidCode) {
      setCountryCodeError("Please select a valid country code");
      return false;
    }
    
    setCountryCodeError("");
    return true;
  };

  const validateAddress = (address: string): boolean => {
    if (!address.trim()) {
      setAddressError("Address is required");
      return false;
    }
    
    // Validate address format - allow letters, numbers, spaces, and common address characters
    const validPattern = /^[a-zA-Z0-9\s\-,.#\/]+$/;
    if (!validPattern.test(address)) {
      setAddressError("Address can only contain letters, numbers, spaces, and characters: -, . # /");
      return false;
    }
    
    setAddressError("");
    return true;
  };

  const validateCountry = (country: string): boolean => {
    if (!country.trim()) {
      setCountryError("Country is required");
      return false;
    }
    setCountryError("");
    return true;
  };

  const validateState = (state: string): boolean => {
    if (!state.trim()) {
      setStateError("State is required");
      return false;
    }
    setStateError("");
    return true;
  };

  const validateCity = (city: string): boolean => {
    if (!city.trim()) {
      setCityError("City is required");
      return false;
    }
    setCityError("");
    return true;
  };

  const validateStreet = (street: string): boolean => {
    if (!street.trim()) {
      setStreetError("Street address is required");
      return false;
    }
    setStreetError("");
    return true;
  };

  const validateZipCode = (zipCode: string): boolean => {
    if (!zipCode.trim()) {
      setZipCodeError("ZIP code is required");
      return false;
    }
    setZipCodeError("");
    return true;
  };

  const validateDateOfBirth = (value: string): boolean => {
    if (!isInvitationSignup && !value.trim()) {
      setDateOfBirthError("Date of birth is required");
      return false;
    }
    setDateOfBirthError("");
    return true;
  };

  const handleEmailOrUsernameSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Block submission if invitation is invalid or still loading
    if (loadingInvitationPreview) {
      return;
    }
    if (formError && isInvitationSignup) {
      return; // Don't proceed if invitation is invalid
    }
    
    setFormError("");
    
    const isFullNameValid = validateFullName(fullName);
    const isEmailValid = validateEmail(email, 'valid_email');
    const isPasswordValid = validatePassword(password);
    const isConfirmPasswordValid = validateConfirmPassword(confirmPassword);
    const isMobileNumberValid = validateMobileNumber(mobileNumber);
    const isCountryCodeValid = validateCountryCode(selectedCountryCode);
    const isSpotifyUrlValid = validateSpotifyUrl(spotifyUrl);
    const isInstagramValid = isInvitationSignup || validateInstagramProfile(instagramUrl);
    const distributorToValidate = distributors.length > 0 ? distributors.join(", ") : "";
    const isCurrentDistributorValid = validateCurrentDistributor(distributorToValidate, 'not_empty');
    const isDobValid = validateDateOfBirth(dateOfBirth);

    if (isInvitationSignup) {
      const isAddressValid = validateAddress(address);
      const isCountryValid = validateCountry(country);
      const isStateValid = validateState(state);
      const isCityValid = validateCity(city);
      const isStreetValid = validateStreet(street);
      const isZipCodeValid = validateZipCode(zipCode);
      if (!isFullNameValid || !isEmailValid || !isPasswordValid || !isConfirmPasswordValid || !isMobileNumberValid || !isCountryCodeValid || !isSpotifyUrlValid || !isCurrentDistributorValid || !isAddressValid || !isCountryValid || !isStateValid || !isCityValid || !isStreetValid || !isZipCodeValid) {
        return;
      }
    } else if (!isFullNameValid || !isEmailValid || !isPasswordValid || !isConfirmPasswordValid || !isMobileNumberValid || !isCountryCodeValid || !isSpotifyUrlValid || !isInstagramValid || !isCurrentDistributorValid || !isDobValid) {
      return;
    }
    
    await handleEmailSignUp(email, password);
  };

  const handleEmailSignUp = async (userEmail: string, userPassword: string) => {
    setLoading(true);
    setSignupInProgress(true); // Mark signup as in progress BEFORE Firebase user creation

    // Track signup submission
    trackEvent(AUTH_EVENTS.SIGNUP_SUBMITTED, { method: 'email' });
    trackHyperDXAction('auth.signup_attempt', { method: 'email' });

    // Wait for any in-progress checks to complete (with timeout)
    let attempts = 0;
    const maxAttempts = 50; // 5 seconds max wait (50 * 100ms)
    while ((emailStatus === 'checking' || mobileStatus === 'checking') && attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, 100));
      attempts++;
    }

    // If checks are still in progress after timeout, proceed anyway (backend will validate)
    if (emailStatus === 'checking' || mobileStatus === 'checking') {
      // Proceed with signup - backend will validate
    }
    
    // Final availability check right before signup to prevent race conditions
    try {
      const checkUsername = isInvitationSignup
        ? toStoredUsername(fullName)
        : toStoredUsername(userEmail);
      const [emailCheck, usernameCheck, mobileCheck] = await Promise.all([
        checkUserAvailabilityForAuth("email", userEmail),
        checkUserAvailabilityForAuth("username", checkUsername),
        checkUserAvailabilityForAuth("mobile", `${selectedCountryCode}${mobileNumber}`)
      ]);
      
      if (emailCheck.exists) {
        setEmailStatus('taken');
        setEmailError("Email is already registered");
        setLoading(false);
        setSignupInProgress(false);
        return;
      }
      
      if (usernameCheck.exists) {
        setFormError("An account with this email or username already exists");
        setLoading(false);
        setSignupInProgress(false);
        return;
      }
      
      if (mobileCheck.exists) {
        setMobileStatus('taken');
        setMobileNumberError("Mobile number is already registered");
        setLoading(false);
        setSignupInProgress(false);
        return;
      }
    } catch (error) {
      console.error("Error in final availability check:", error);
      // Continue with signup if check fails - backend will validate anyway
    }
    
    try {
      const derivedUsername = isInvitationSignup
        ? toStoredUsername(fullName)
        : toStoredUsername(userEmail);
      const userCredential = await createUser(userEmail, userPassword, derivedUsername, spotifyUrl, distributors.join(', '));
      if (userCredential && userCredential.user) {
        setSignupInProgress(false);

        trackEvent(AUTH_EVENTS.SIGNUP_SUCCESS, { method: 'email' });
        setUserId(userCredential.user.uid);
        setHyperDXUser(userCredential.user.uid, email);
        trackHyperDXAction('auth.signup_success', { method: 'email', hasInvitation: !!invitationToken });

        if (!isInvitationSignup) {
          // Public signup is a request: do not enter the app until admin approves.
          setAuthStatus(false);
          try { await auth.signOut(); } catch { /* ignore */ }
          toast.success('Access requested. We will notify you when an admin reviews your request.');
          navigate('/login');
          return;
        }

        setAuthStatus(true);
        triggerProfileFetch();
        navigate(config.DefaultRoutePage);
      } else {
        setAuthStatus(false);
        setSignupInProgress(false); // Mark signup as failed
        trackEvent(AUTH_EVENTS.SIGNUP_ERROR, { method: 'email', error_type: 'user_creation_failed' });
        trackHyperDXAction('auth.signup_error', { method: 'email', errorCode: 'user_creation_failed' });
        //setFormError('Failed to create user account');
        return;
      }

    } catch (error) {
      setAuthStatus(false);
      setSignupInProgress(false); // Mark signup as failed
      const authError = error as AuthError;
      trackEvent(AUTH_EVENTS.SIGNUP_ERROR, { method: 'email', error_type: authError.code || 'unknown' });
      trackHyperDXAction('auth.signup_error', { method: 'email', errorCode: authError.code || 'unknown' });
      setFormError(getFirebaseErrorMessage(authError));
    } finally {
      setLoading(false);
    }
  }

  const createUser = async (userEmail: string, userPassword: string, userName: string, spotifyUrl: string, currentDistributor: string) => {
    try {
      // First, try to sign in with existing credentials
      // try {
      //   const existingUserCredential = await signInWithEmailAndPassword(auth, userEmail, userPassword);
      //   // User already exists, just return their credentials
      //   return existingUserCredential;
      // } catch (signInError: any) {
      //   // If sign in fails with user-not-found, proceed to create new user
      //   if (signInError.code !== 'auth/user-not-found' && signInError.code !== 'auth/wrong-password') {
      //     throw signInError; // Re-throw other errors
      //   }
      // }

      // Create new user only if they don't exist
      const userCredential: any = await createUserWithEmailAndPassword(auth, userEmail, userPassword);
      //console.log(userCredential.user);
      const user = await createUserIfNotExists({
        fbuid: userCredential.user.uid,
        email: userEmail,
        username: userName,
        firstname: fullName?.split(' ')[0] || '',
        lastname: fullName?.split(' ').slice(1).join(' ') || '',
        artistname: fullName, // Use full name as artist name
        mobile: `${selectedCountryCode}${mobileNumber}`,
        spotifyUrl: spotifyUrl,
        youtubeUrl: youtubeUrl || undefined,
        appleUrl: appleUrl || undefined,
        instagramUrl: normalizeInstagramProfile(instagramUrl) || undefined,
        tiktokUrl: tiktokUrl || undefined,
        distributor: (() => {
          // Join distributors array with comma and space
          return distributors.join(', ');
        })(),
        address: isInvitationSignup ? address : undefined,
        country: isInvitationSignup ? country : undefined,
        state: isInvitationSignup ? state : undefined,
        city: isInvitationSignup ? city : undefined,
        street: isInvitationSignup ? street : undefined,
        zipCode: isInvitationSignup ? zipCode : undefined,
        dateOfBirth: dateOfBirth || undefined,
        invitationToken: invitationToken || undefined, // Include token if present
      });
      if (user && user.id > 0) {
        return userCredential;
      }else{
        throw new Error("Something went wrong. Please try again later.")
      }
    } catch (error: any) {
      setAuthStatus(false);
      
      //Delete user from Firebase
      try {
        await auth.currentUser?.delete(); // may fail due to token staleness
      } catch (deleteErr: any) {
        console.error("Error deleting Firebase user:", deleteErr);
      }
      await auth.signOut();
      
      // Handle 400 Bad Request errors - show the error message from response
      if (error?.status === 400 && error?.message) {
        setFormError(error.message);
        return;
      }
      
      // Handle Firebase auth errors
      if (error?.code === 'auth/email-already-in-use') {
        setFormError("Email already in use");
      } else if (error?.code) {
        // Firebase auth error
        const authError = error as AuthError;
        setFormError(getFirebaseErrorMessage(authError));
      } else {
        // Generic error - use message if available, otherwise default
        setFormError(error?.message || "Something went wrong. Please try again later.");
      }
    }
  }

  // const handleAppleSignUp = async () => {
  //   setLoading(true);
  //   try {
  //     const provider = new OAuthProvider("apple.com");
  //     provider.addScope("email");
  //     provider.addScope("name");
  //     provider.setCustomParameters({ prompt: "consent" });
  //     const userCredential = await signInWithPopup(auth, provider);
  //     if (!userCredential.user.email) {
  //       throw new Error("Email not found");
  //     }
  //     const user = await createUserIfNotExists({
  //       fbuid: userCredential.user.uid,
  //       email: userCredential.user.email,
  //       username: userCredential.user.email,
  //       firstname: userCredential.user.displayName?.split(' ')[0] || '',
  //       lastname: userCredential.user.displayName?.split(' ').slice(1).join(' ') || '',
  //     });
  //     if (user && user.id > 0) {
  //       setAuthStatus(true);
  //       navigate(config.DefaultRoutePage);
  //     }else{
  //       throw new Error("Something went wrong. Please try again later.")
  //     }
  //   } catch (error) {
  //     setAuthStatus(false);
  //     console.log("User not created successfully", error);
  //     //Delete user from Firebase
  //     try {
  //       await auth.currentUser?.delete(); // may fail due to token staleness
  //     } catch (deleteErr: any) {
  //       console.error("Error deleting Apple user:", deleteErr);
  //     }
  //     await auth.signOut();
  //     const authError = error as AuthError;
  //     setFormError(getFirebaseErrorMessage(authError));
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // const handleGoogleSignUp = async () => {
  //   setLoading(true);
  //   const provider = new GoogleAuthProvider();
  //   try {
  //     const userCredential = await signInWithPopup(auth, provider);
  //     if (!userCredential.user.email) {
  //       throw new Error("Email not found");
  //     }
  //     const user = await createUserIfNotExists({
  //       fbuid: userCredential.user.uid,
  //       email: userCredential.user.email,
  //       username: userCredential.user.email,
  //       firstname: userCredential.user.displayName?.split(' ')[0] || '',
  //       lastname: userCredential.user.displayName?.split(' ').slice(1).join(' ') || '',
  //     });
  //     if (user && user.id > 0) {
  //       setAuthStatus(true);
  //       navigate(config.DefaultRoutePage);
  //     }else{
  //       throw new Error("Something went wrong. Please try again later.")
  //     }
  //   } catch (error) {
  //     setAuthStatus(false);
  //     console.log("User not created successfully", error);
  //     //Delete user from Firebase
  //     try {
  //       await auth.currentUser?.delete(); // may fail due to token staleness
  //     } catch (deleteErr: any) {
  //       console.error("Error deleting Apple user:", deleteErr);
  //     }
  //     await auth.signOut();
  //     const authError = error as AuthError;
  //     setFormError(getFirebaseErrorMessage(authError));
  //   } finally {
  //     setLoading(false);
  //   }
  // };
  
  // Helper function to sync Firebase user with Supabase
  // const signupUser = async (user: User) => {
  //   if (!user || !user.uid || !user.email) return;
    
  //   try {
  //     // Extract user details from Firebase user
  //     const userData = {
  //       fbuid: user.uid,
  //       email: user.email,
  //       firstname: user.displayName?.split(' ')[0] || '',
  //       lastname: user.displayName?.split(' ').slice(1).join(' ') || '',
  //       username: ""
  //     };
      
  //     // Create user in Supabase if they don't exist
  //     await createUserIfNotExists(userData);
  //   } catch (error) {
  //     console.error("Error syncing user with Supabase:", error);
  //     // Don't block the login flow if this fails
  //   }
  // };

  // const AppleIcon = () => (
  //   <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
  //     <path d="M16.365 0c-.846.038-1.854.6-2.451 1.308-.53.62-.99 1.57-.814 2.5.947.065 1.92-.487 2.485-1.183.57-.707.985-1.642.78-2.625zM21.963 17.78c-.55 1.364-.8 1.88-1.5 3.035-.98 1.57-2.37 3.526-4.185 3.534-1.66.015-2.08-1.026-4.32-1.02-2.23.007-2.7 1.033-4.36 1.018-1.81-.007-3.28-1.773-4.26-3.344C1.665 18.94.598 14.683 2.39 11.363c1.15-2.123 2.965-3.357 4.76-3.37 1.84-.015 3.01 1.132 4.52 1.163 1.46.03 2.37-1.168 4.51-1.188 1.56-.014 3.2.85 4.37 2.326-3.82 2.11-3.17 7.68-.59 9.485z"/>
  //   </svg>
  // );

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Block submission if invitation is invalid or still loading
    if (loadingInvitationPreview) {
      return;
    }
    if (formError && isInvitationSignup) {
      return; // Don't proceed if invitation is invalid
    }
    
    setFormError("");
    
    const isFullNameValid = validateFullName(fullName);
    const isEmailValid = validateEmail(email, 'valid_email');
    const isPasswordValid = validatePassword(password);
    const isConfirmPasswordValid = validateConfirmPassword(confirmPassword);
    const isMobileNumberValid = validateMobileNumber(mobileNumber);
    const isCountryCodeValid = validateCountryCode(selectedCountryCode);
    const isSpotifyUrlValid = validateSpotifyUrl(spotifyUrl);
    const isInstagramValid = isInvitationSignup || validateInstagramProfile(instagramUrl);
    const distributorToValidate = distributors.length > 0 ? distributors.join(", ") : "";
    const isCurrentDistributorValid = validateCurrentDistributor(distributorToValidate, 'not_empty');
    const isDobValid = validateDateOfBirth(dateOfBirth);

    if (isInvitationSignup) {
      const isAddressValid = validateAddress(address);
      const isCountryValid = validateCountry(country);
      const isStateValid = validateState(state);
      const isCityValid = validateCity(city);
      const isStreetValid = validateStreet(street);
      const isZipCodeValid = validateZipCode(zipCode);
      if (!isFullNameValid || !isEmailValid || !isPasswordValid || !isConfirmPasswordValid || !isMobileNumberValid || !isCountryCodeValid || !isSpotifyUrlValid || !isCurrentDistributorValid || !isAddressValid || !isCountryValid || !isStateValid || !isCityValid || !isStreetValid || !isZipCodeValid) {
        return;
      }
    } else if (!isFullNameValid || !isEmailValid || !isPasswordValid || !isConfirmPasswordValid || !isMobileNumberValid || !isCountryCodeValid || !isSpotifyUrlValid || !isInstagramValid || !isCurrentDistributorValid || !isDobValid) {
      return;
    }
    
    await handleEmailSignUp(email, password);
  };

  return (
    <div className="min-h-screen w-full flex flex-col items-center px-4 sm:px-6 bg-nova-color">
      <form onSubmit={handleFormSubmit} className="w-full max-w-md flex flex-col items-center space-y-6 p-6 bg-nova-color login-form mt-8 sm:mt-32">
        
        {/* Logo */}
        <Link to="/login" className="flex-shrink-0">
        <img src="/assets/images/Nova.png" alt="Nova" className="h-10 w-auto mb-6 sm:mb-8" />
        </Link>
        <div className="w-full sm:w-[25.8125rem]">
          <input
            type="text"
            placeholder="Full Legal Name"
            className={`w-full border-b ${fullNameError ? 'border-red-500' : 'border-gray-300'} px-0 py-2 text-sm focus:outline-none placeholder:font-normal font-family-messina-sans-regular`}
            style={{ backgroundColor: '#F8F7F2', '--tw-placeholder-color': '#939393' } as React.CSSProperties}
            value={fullName}
            onChange={(e) => {
              setFullName(e.target.value);
              if (fullNameError) validateFullName(e.target.value);
            }}
            onFocus={trackSignupStarted}
            onBlur={() => validateFullName(fullName)}
          />
          {fullNameError && <p className="text-red-500 text-xs mt-1">{fullNameError}</p>}
        </div>
        {/* Email Input */}
        <div className="w-full sm:w-[25.8125rem]">
          <div className="relative">
            {loadingInvitationPreview ? (
              <div className="w-full border-b border-gray-300 px-0 py-2 text-sm text-gray-500 font-family-messina-sans-regular">
                Loading invitation details...
              </div>
            ) : (
              <input
                type="text"
                placeholder="Email"
                disabled={emailDisabled}
                className={`w-full border-b ${emailError ? 'border-red-500' : emailStatus === 'available' ? 'border-green-500' : 'border-gray-300'} px-0 py-2 text-sm focus:outline-none placeholder:font-normal font-family-messina-sans-regular ${
                  emailDisabled ? 'bg-gray-100 cursor-not-allowed opacity-100' : ''
                }`}
                style={{ backgroundColor: emailDisabled ? '#E5E7EB' : '#F8F7F2', '--tw-placeholder-color': '#939393' } as React.CSSProperties}
                value={email}
                onChange={(e) => {
                  if (emailDisabled) return; // Prevent editing when disabled
                  
                  const value = e.target.value;
                  setEmail(value);
                  
                  // Clear previous error when user starts typing
                  if (emailError) {
                    setEmailError("");
                  }
                  
                  // Reset status when user clears the field
                  if (value.length === 0) {
                    setEmailStatus('idle');
                  } else {
                    // Basic email format check
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    if (emailRegex.test(value)) {
                      // Trigger debounced check for valid email formats
                      debouncedCheckEmail(value);
                    } else {
                      // For invalid email formats, just validate locally
                      setEmailStatus('idle');
                      if (value.length > 0) {
                        setEmailError("Please enter a valid email");
                      }
                    }
                  }
                }}
                onBlur={() => !emailDisabled && validateEmail(email, 'valid_email')}
              />
            )}
            {/* Status indicator */}
            {!loadingInvitationPreview && emailStatus === 'checking' && (
              <div className="absolute right-0 top-1/2 transform -translate-y-1/2">
                <div className="h-4 w-4 animate-spin border-2 border-gray-300 border-t-gray-600 rounded-full"></div>
              </div>
            )}
            {!loadingInvitationPreview && emailStatus === 'available' && email.length > 0 && (
              <div className="absolute right-0 top-1/2 transform -translate-y-1/2">
                <svg className="h-4 w-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            )}
            {!loadingInvitationPreview && emailStatus === 'taken' && (
              <div className="absolute right-0 top-1/2 transform -translate-y-1/2">
                <svg className="h-4 w-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            )}
            {emailDisabled && email.length > 0 && !loadingInvitationPreview && (
              <div className="absolute right-0 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
                <span className="text-xs text-gray-500 font-family-messina-sans">From invitation</span>
              </div>
            )}
          </div>
          {emailError && <p className="text-red-500 text-xs mt-1">{emailError}</p>}
          {emailStatus === 'available' && email.length > 0 && !emailError && (
            <p className="text-green-500 text-xs mt-1">Email is available</p>
          )}
        </div>

        {/* Password Input */}
        <div className="w-full sm:w-[25.8125rem]">
          <input
            type="password"
            placeholder="Password"
            className={`w-full border-b ${passwordError ? 'border-red-500' : 'border-gray-300'} px-0 py-2 text-sm focus:outline-none placeholder:font-normal font-family-messina-sans-regular`}
            style={{ 
              backgroundColor: '#F8F7F2', 
              '--tw-placeholder-color': '#939393',
              letterSpacing: '0.05em',
              height: '2.5rem'
            } as React.CSSProperties}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (passwordError) validatePassword(e.target.value);
            }}
            onBlur={() => validatePassword(password)}
          />
          {passwordError && <p className="text-red-500 text-xs mt-1">{passwordError}</p>}
          {/* <div className="w-full text-right mt-2">
            <Link to="/forgot-password" className="text-sm text-blue-600 hover:underline">
              Forgot Password?
            </Link>
          </div> */}
        </div>

        <div className="w-full sm:w-[25.8125rem]">
          <input
            type="password"
            placeholder="Confirm Password"
            className={`w-full border-b ${passwordError ? 'border-red-500' : 'border-gray-300'} px-0 py-2 text-sm focus:outline-none placeholder:font-normal font-family-messina-sans-regular`}
            style={{ 
              backgroundColor: '#F8F7F2', 
              '--tw-placeholder-color': '#939393',
              letterSpacing: '0.05em',
              height: '2.5rem'
            } as React.CSSProperties}
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              if (confirmPasswordError) validateConfirmPassword(e.target.value);
            }}
            onBlur={() => validateConfirmPassword(confirmPassword)}
          />
          {confirmPasswordError && <p className="text-red-500 text-xs mt-1">{confirmPasswordError}</p>}
        </div>

        {/* Mobile Number Input */}
        <div className="w-full sm:w-[25.8125rem]">
          <div className="flex gap-2">
            {/* Country Code Dropdown */}
            <div className="flex-shrink-0">
              <CountryCodeDropdown
                value={selectedCountryCode}
                onChange={(value) => {
                  setSelectedCountryCode(value);
                  if (countryCodeError) validateCountryCode(value);
                  // Re-check mobile availability when country code changes with new country code
                  if (mobileNumber && mobileNumber.length >= 8 && mobileNumber.length <= 15) {
                    debouncedCheckMobile(mobileNumber, value);
                  }
                }}
                className="w-full"
                style={{ 
                  minWidth: '120px',
                  height: '2.5rem'
                }}
                error={!!countryCodeError}
              />
            </div>
            
            {/* Mobile Number Input */}
            <div className="flex-1">
              <input
                type="tel"
                placeholder="Mobile Number"
                className={`w-full border-b ${mobileNumberError ? 'border-red-500' : 'border-gray-300'} px-0 py-2 text-sm focus:outline-none placeholder:font-normal font-family-messina-sans-regular`}
                style={{ 
                  backgroundColor: '#F8F7F2', 
                  '--tw-placeholder-color': '#939393',
                  height: '2.5rem'
                } as React.CSSProperties}
                value={mobileNumber}
                onChange={(e) => {
                  // Remove all non-digit characters and limit to 12 digits
                  const digits = e.target.value.replace(/\D/g, '').slice(0, 12);
                  setMobileNumber(digits);
                  if (mobileNumberError) {
                    setMobileNumberError("");
                  }
                  // Check availability for 10-12 digits
                  if (digits.length >= 10 && digits.length <= 12) {
                    debouncedCheckMobile(digits, selectedCountryCode);
                  } else {
                    setMobileStatus('idle');
                  }
                }}
                onBlur={() => validateMobileNumber(mobileNumber)}
                maxLength={12}
              />
            </div>
          </div>
          {mobileNumberError && <p className="text-red-500 text-xs mt-1">{mobileNumberError}</p>}
          {countryCodeError && <p className="text-red-500 text-xs mt-1">{countryCodeError}</p>}
        </div>

        {!isInvitationSignup && (
        <div className="w-full sm:w-[25.8125rem]">
          <input
            type="date"
            placeholder="Date of birth"
            className={`w-full border-b ${dateOfBirthError ? 'border-red-500' : 'border-gray-300'} px-0 py-2 text-sm focus:outline-none placeholder:font-normal font-family-messina-sans-regular`}
            style={{ backgroundColor: '#F8F7F2' } as React.CSSProperties}
            value={dateOfBirth}
            onChange={(e) => {
              setDateOfBirth(e.target.value);
              if (dateOfBirthError) validateDateOfBirth(e.target.value);
            }}
            onBlur={() => validateDateOfBirth(dateOfBirth)}
          />
          {dateOfBirthError && <p className="text-red-500 text-xs mt-1">{dateOfBirthError}</p>}
          <p className="text-xs text-gray-500 mt-1">Date of birth</p>
        </div>
        )}

        <div className="w-full sm:w-[25.8125rem]">
          <input
            type="text"
            placeholder="Spotify URL"
            className={`w-full border-b ${spotifyUrlError ? 'border-red-500' : 'border-[#585858]'} px-0 py-2 text-sm focus:outline-none placeholder:font-normal font-family-messina-sans-regular`}
            style={{ backgroundColor: '#F8F7F2', '--tw-placeholder-color': '#939393', marginBottom: "0.5rem" } as React.CSSProperties}
            value={spotifyUrl}
            onChange={(e) => {
              setSpotifyUrl(e.target.value);
              if (spotifyUrlError) validateSpotifyUrl(e.target.value);
            }}
            onBlur={() => validateSpotifyUrl(spotifyUrl)}
          />
          <Link to="https://support.spotify.com/us/artists/article/finding-your-artist-track-release-links/" target="_blank" style={{ fontSize: "0.7rem" }} className="text-sm text-[#939393] hover:underline">
            Where can I find this?
          </Link>
          <p className="text-xs text-gray-500 mt-1 font-family-messina-sans-regular">
            If you are part of an artists management team please add the artists spotify URL
          </p>
          {spotifyUrlError && <p className="text-red-500 text-xs mt-1">{spotifyUrlError}</p>}
        </div>

        {!isInvitationSignup && (
        <>
        <div className="w-full sm:w-[25.8125rem]">
          <input
            type="text"
            placeholder="Instagram username or profile URL"
            aria-required="true"
            aria-invalid={Boolean(instagramUrlError)}
            className={`w-full border-b ${instagramUrlError ? 'border-red-500' : 'border-gray-300'} px-0 py-2 text-sm focus:outline-none placeholder:font-normal font-family-messina-sans-regular`}
            style={{ backgroundColor: '#F8F7F2' } as React.CSSProperties}
            value={instagramUrl}
            onChange={(e) => {
              setInstagramUrl(e.target.value);
              if (instagramUrlError) validateInstagramProfile(e.target.value);
            }}
            onBlur={() => {
              if (validateInstagramProfile(instagramUrl)) {
                setInstagramUrl(normalizeInstagramProfile(instagramUrl) || instagramUrl);
              }
            }}
          />
          {instagramUrlError && <p className="text-red-500 text-xs mt-1">{instagramUrlError}</p>}
        </div>
        <div className="w-full sm:w-[25.8125rem]">
          <input type="text" placeholder="Apple Music URL (optional)" className="w-full border-b border-gray-300 px-0 py-2 text-sm focus:outline-none placeholder:font-normal font-family-messina-sans-regular" style={{ backgroundColor: '#F8F7F2' } as React.CSSProperties} value={appleUrl} onChange={(e) => setAppleUrl(e.target.value)} />
        </div>
        <div className="w-full sm:w-[25.8125rem]">
          <input type="text" placeholder="YouTube URL (optional)" className="w-full border-b border-gray-300 px-0 py-2 text-sm focus:outline-none placeholder:font-normal font-family-messina-sans-regular" style={{ backgroundColor: '#F8F7F2' } as React.CSSProperties} value={youtubeUrl} onChange={(e) => setYoutubeUrl(e.target.value)} />
        </div>
        <div className="w-full sm:w-[25.8125rem]">
          <input type="text" placeholder="TikTok URL (optional)" className="w-full border-b border-gray-300 px-0 py-2 text-sm focus:outline-none placeholder:font-normal font-family-messina-sans-regular" style={{ backgroundColor: '#F8F7F2' } as React.CSSProperties} value={tiktokUrl} onChange={(e) => setTiktokUrl(e.target.value)} />
        </div>
        </>
        )}

        <div className="w-full sm:w-[25.8125rem]">
          <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
            DISTRIBUTORS
          </label>
          
          {/* Display existing distributors as tags */}
          {distributors.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {distributors.map((distributor, index) => (
                <span
                  key={index}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-900 rounded-full text-sm font-family-messina-sans-regular"
                >
                  {distributor}
                  <button
                    type="button"
                    onClick={() => removeDistributor(index)}
                    className="ml-1 text-gray-500 hover:text-gray-700 focus:outline-none"
                    aria-label={`Remove ${distributor}`}
                  >
                    <X size={14} />
                  </button>
                </span>
              ))}
            </div>
          )}
          
          {/* Add Distributor - Dropdown Only */}
          <div className="space-y-3">
            <select
              value={selectedDistributorDropdown}
              onChange={(e) => handleDistributorSelect(e.target.value)}
              className={`w-full border-b ${currentDistributorError ? 'border-red-500' : 'border-[#585858]'} px-0 py-2 text-sm focus:outline-none font-family-messina-sans-regular`}
              style={{ backgroundColor: '#F8F7F2', marginBottom: "0.5rem" } as React.CSSProperties}
            >
              <option value="">Select distributor to add</option>
              {PLATFORMS.filter(platform => 
                platform.name !== "Other" && 
                !distributors.some(d => d.toLowerCase() === platform.name.toLowerCase())
              ).map((platform) => (
                <option key={platform.id} value={platform.name}>
                  {platform.name}
                </option>
              ))}
              <option value="Other">Other</option>
            </select>
            
            {/* Custom Distributor Input - Only show when "Other" is selected */}
            {selectedDistributorDropdown === "Other" && (
              <div className="flex gap-2">
                <input
                  type="text"
                  value={customDistributorInput}
                  onChange={(e) => setCustomDistributorInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddCustomDistributor();
                    }
                  }}
                  placeholder="Enter custom distributor name"
                  className={`flex-1 border-b ${currentDistributorError ? 'border-red-500' : 'border-[#585858]'} px-0 py-2 text-sm focus:outline-none font-family-messina-sans-regular`}
                  style={{ backgroundColor: '#F8F7F2' } as React.CSSProperties}
                />
                <button
                  type="button"
                  onClick={handleAddCustomDistributor}
                  className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-family-messina-sans-regular"
                >
                  Add
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedDistributorDropdown("");
                    setCustomDistributorInput("");
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-family-messina-sans-regular"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
          
          {currentDistributorError && <p className="text-red-500 text-xs mt-1">{currentDistributorError}</p>}
          {distributors.length === 0 && (
            <p className="text-gray-500 text-xs mt-2">No distributors added yet</p>
          )}
        </div>

        {isInvitationSignup && (
        <>
        {/* Address Input */}
        <div className="w-full sm:w-[25.8125rem]">
          <input
            type="text"
            placeholder="Address"
            className={`w-full border-b ${addressError ? 'border-red-500' : 'border-gray-300'} px-0 py-2 text-sm focus:outline-none placeholder:font-normal font-family-messina-sans-regular`}
            style={{ backgroundColor: '#F8F7F2', '--tw-placeholder-color': '#939393' } as React.CSSProperties}
            value={address}
            onChange={(e) => {
              setAddress(e.target.value);
              if (addressError) validateAddress(e.target.value);
            }}
            onBlur={() => validateAddress(address)}
          />
          {addressError && <p className="text-red-500 text-xs mt-1">{addressError}</p>}
        </div>

        {/* Address Fields - 2 columns */}
        <div className="w-full sm:w-[25.8125rem]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {/* Country */}
            <div>
              <input
                type="text"
                placeholder="Country"
                className={`w-full border-b ${countryError ? 'border-red-500' : 'border-gray-300'} px-0 py-2 text-sm focus:outline-none placeholder:font-normal font-family-messina-sans-regular`}
                style={{ backgroundColor: '#F8F7F2', '--tw-placeholder-color': '#939393' } as React.CSSProperties}
                value={country}
                onChange={(e) => {
                  setCountry(e.target.value);
                  if (countryError) validateCountry(e.target.value);
                }}
                onBlur={() => validateCountry(country)}
              />
              {countryError && <p className="text-red-500 text-xs mt-1">{countryError}</p>}
            </div>

            {/* State */}
            <div>
              <input
                type="text"
                placeholder="State"
                className={`w-full border-b ${stateError ? 'border-red-500' : 'border-gray-300'} px-0 py-2 text-sm focus:outline-none placeholder:font-normal font-family-messina-sans-regular`}
                style={{ backgroundColor: '#F8F7F2', '--tw-placeholder-color': '#939393' } as React.CSSProperties}
                value={state}
                onChange={(e) => {
                  setState(e.target.value);
                  if (stateError) validateState(e.target.value);
                }}
                onBlur={() => validateState(state)}
              />
              {stateError && <p className="text-red-500 text-xs mt-1">{stateError}</p>}
            </div>

            {/* City */}
            <div>
              <input
                type="text"
                placeholder="City"
                className={`w-full border-b ${cityError ? 'border-red-500' : 'border-gray-300'} px-0 py-2 text-sm focus:outline-none placeholder:font-normal font-family-messina-sans-regular`}
                style={{ backgroundColor: '#F8F7F2', '--tw-placeholder-color': '#939393' } as React.CSSProperties}
                value={city}
                onChange={(e) => {
                  setCity(e.target.value);
                  if (cityError) validateCity(e.target.value);
                }}
                onBlur={() => validateCity(city)}
              />
              {cityError && <p className="text-red-500 text-xs mt-1">{cityError}</p>}
            </div>

            {/* Street */}
            <div>
              <input
                type="text"
                placeholder="Street"
                className={`w-full border-b ${streetError ? 'border-red-500' : 'border-gray-300'} px-0 py-2 text-sm focus:outline-none placeholder:font-normal font-family-messina-sans-regular`}
                style={{ backgroundColor: '#F8F7F2', '--tw-placeholder-color': '#939393' } as React.CSSProperties}
                value={street}
                onChange={(e) => {
                  setStreet(e.target.value);
                  if (streetError) validateStreet(e.target.value);
                }}
                onBlur={() => validateStreet(street)}
              />
              {streetError && <p className="text-red-500 text-xs mt-1">{streetError}</p>}
            </div>

            {/* ZIP Code */}
            <div>
              <input
                type="text"
                placeholder="ZIP Code"
                className={`w-full border-b ${zipCodeError ? 'border-red-500' : 'border-gray-300'} px-0 py-2 text-sm focus:outline-none placeholder:font-normal font-family-messina-sans-regular`}
                style={{ backgroundColor: '#F8F7F2', '--tw-placeholder-color': '#939393' } as React.CSSProperties}
                value={zipCode}
                onChange={(e) => {
                  setZipCode(e.target.value);
                  if (zipCodeError) validateZipCode(e.target.value);
                }}
                onBlur={() => validateZipCode(zipCode)}
              />
              {zipCodeError && <p className="text-red-500 text-xs mt-1">{zipCodeError}</p>}
            </div>
          </div>
        </div>

        </>
        )}
        
        {/* Form Error Message */}
        {formError && (
          <div className={`w-full sm:w-[25.8125rem] text-red-500 text-sm mt-2 p-3 rounded-lg border border-red-300 bg-red-50 font-family-messina-sans ${
            isInvitationSignup ? 'font-semibold' : ''
          }`}>
            {formError}
          </div>
        )}

        {/* Sign In Button */}
        <button
          type="submit"
          className="w-full sm:w-[25.8125rem] border px-5 py-3 sm:py-4 rounded-full text-sm sm:text-base text-black font-medium hover:bg-gray-200 transition flex items-center justify-center gap-3 uppercase min-h-[3rem] navbar-menu-item"
          style={{ paddingTop: "1rem" }}
          disabled={loading || emailStatus === 'checking' || mobileStatus === 'checking' || emailStatus === 'taken' || mobileStatus === 'taken'}
        >
          {loading ? (
        <div className="h-5 w-5 animate-spin border-2 border-t-transparent border-black rounded-full" />
      ) : (
        <>
          {/* <img src="/assets/images/email-icon.svg" alt="Email Icon" className="h-5 w-5" />
          Sign Up with Email */}
          CONTINUE
        </>
      )}
        </button>

        {/* Divider */}
        {/* <div className="w-full sm:w-[25.5625rem] h-[0.1875rem] bg-nova-divider-200"></div> */}

        {/* Sign in with Apple */}
        {/* <button onClick={handleAppleSignUp} className="w-full sm:w-[25.8125rem] border px-5 py-3 sm:py-4 rounded-full flex items-center justify-center text-sm sm:text-base text-black font-medium hover:bg-gray-200 transition gap-3 uppercase min-h-[3rem]">
          <img src="/assets/images/apple-icon-3.svg" alt="Apple Icon" className="h-5 w-5" />
          <span style={{ paddingTop: "6px" }}> Continue with Apple</span>
        </button> */}

        {/* Sign in with Google */}
        {/* <button
          onClick={handleGoogleSignUp}
          className="w-full sm:w-[25.8125rem] border px-5 py-3 sm:py-4 rounded-full flex items-center justify-center text-sm sm:text-base text-black font-medium hover:bg-gray-200 transition gap-3 uppercase min-h-[3rem]"
          style={{ paddingTop: "1rem" }}
        >
          <img src="/assets/images/google-icon-2.svg" alt="Google Logo" className="h-5 w-5" />
          Continue with Google
        </button> */}

        {/* Join Waitlist */}
        <div className="w-full text-center mt-2">
          <Link to="mailto:contact@marsmarkets.com?subject=Contact from Mars Website" className="text-[#939393] hover:underline font-family-messina-sans-regular" style={{ fontSize: "0.625rem" }}>
            Questions? Contact us
          </Link>
        </div>
      </form>
    </div>
  );
};

export default SignupForm;
